/**
 * BarraQuota — quanto della quota compresa è stato usato, e quanto si è andati oltre (atlante cap. 251, tappa 4).
 * Dentro la quota la barra è nell'inchiostro della console; oltre, la parte in più è ambra: è quella che costa.
 * Per i servizi solo a consumo (si contano i centesimi, senza quota) la barra è la spesa sul tetto, se c'è.
 */

import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { tokens } from '../../theme';
import type { UnitaDiConsumo } from '@shared/strumenti/listino';
import { eurDaCentesimi, euro, unita } from './testiStrumenti';

export const BarraQuota: React.FC<{
  usate: number; quota: number; unitaDiConsumo: UnitaDiConsumo; larghezza?: number | string;
  /** Solo per i servizi a consumo: il tetto del mese in euro (null o assente = nessun tetto). */
  tettoEur?: number | null;
}> = ({ usate, quota, unitaDiConsumo, larghezza = '100%', tettoEur = null }) => {
  if (unitaDiConsumo === 'centesimi' && quota === 0) {
    const pieno = tettoEur && tettoEur > 0 ? Math.min(1, usate / (tettoEur * 100)) : 0;
    return (
      <Stack spacing={0.5} sx={{ width: larghezza, minWidth: 140 }}>
        <Box sx={{ position: 'relative', height: 8, borderRadius: tokens.radius.pill, bgcolor: tokens.color.lineSoft, overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pieno * 100}%`, bgcolor: pieno >= 1 ? tokens.color.warning : tokens.color.graphite }} />
        </Box>
        <Typography variant="caption" sx={{ color: tokens.color.ash, fontVariantNumeric: 'tabular-nums' }}>
          {eurDaCentesimi(usate)} spesi{tettoEur !== null ? ` su ${euro(tettoEur)}` : ''}
        </Typography>
      </Stack>
    );
  }
  const fondo = Math.max(usate, quota, 1);
  const dentro = Math.min(usate, quota) / fondo;
  const oltre = Math.max(0, usate - quota) / fondo;
  const segnaQuota = quota / fondo;
  return (
    <Stack spacing={0.5} sx={{ width: larghezza, minWidth: 140 }}>
      <Box sx={{ position: 'relative', height: 8, borderRadius: tokens.radius.pill, bgcolor: tokens.color.lineSoft, overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${dentro * 100}%`, bgcolor: tokens.color.graphite }} />
        <Box sx={{ position: 'absolute', left: `${segnaQuota * 100}%`, top: 0, bottom: 0, width: `${oltre * 100}%`, bgcolor: tokens.color.warning }} />
        {usate > quota && <Box sx={{ position: 'absolute', left: `${segnaQuota * 100}%`, top: 0, bottom: 0, width: 2, bgcolor: tokens.color.paper }} />}
      </Box>
      <Typography variant="caption" sx={{ color: tokens.color.ash, fontVariantNumeric: 'tabular-nums' }}>
        {usate} su {quota} {unita(unitaDiConsumo, quota)}{usate > quota ? `, ${usate - quota} oltre` : ''}
      </Typography>
    </Stack>
  );
};
